/**
 * EEOC Training Reminder Banner
 * Shows alerts for users who haven't completed mandatory EEOC training
 * Dismissible for 24 hours
 */
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { 
  Shield, 
  AlertTriangle, 
  Clock, 
  XCircle, 
  X,
  ArrowRight
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const DISMISS_STORAGE_KEY = 'eeoc_reminder_dismissed';
const DISMISS_DURATION = 24 * 60 * 60 * 1000; // 24 hours in ms

export default function EEOCReminderBanner({ userId, tenantId, userUrl, className }) {
  const [reminderData, setReminderData] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkDismissStatus();
    if (userId && tenantId) {
      fetchTrainingStatus();
    }
  }, [userId, tenantId]);

  const checkDismissStatus = () => {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissedAt) {
      const dismissTime = parseInt(dismissedAt, 10);
      if (Date.now() - dismissTime < DISMISS_DURATION) {
        setIsDismissed(true);
      } else {
        localStorage.removeItem(DISMISS_STORAGE_KEY);
      }
    }
  };

  const fetchTrainingStatus = async () => {
    try {
      const { data } = await api.get(`/eeoc/user/training-status?user_id=${userId}&tenant_id=${tenantId}`);
      if (data.reminder_needed) {
        setReminderData(data);
      }
    } catch (error) {
      console.log('EEOC training status not available');
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_STORAGE_KEY, Date.now().toString());
    setIsDismissed(true);
  };

  if (loading || isDismissed || !reminderData?.reminder_needed) {
    return null;
  }

  const { urgency, days_remaining } = reminderData;

  // Determine banner style based on urgency
  const bannerStyles = {
    warning: {
      bg: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800',
      icon: Clock,
      iconColor: 'text-amber-600 dark:text-amber-400',
      textColor: 'text-amber-800 dark:text-amber-200',
      buttonClass: 'bg-amber-600 hover:bg-amber-700 text-white'
    },
    urgent: {
      bg: 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800',
      icon: AlertTriangle,
      iconColor: 'text-orange-600 dark:text-orange-400',
      textColor: 'text-orange-800 dark:text-orange-200',
      buttonClass: 'bg-orange-600 hover:bg-orange-700 text-white'
    },
    overdue: {
      bg: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800',
      icon: XCircle,
      iconColor: 'text-red-600 dark:text-red-400',
      textColor: 'text-red-800 dark:text-red-200',
      buttonClass: 'bg-red-600 hover:bg-red-700 text-white'
    }
  };

  const style = bannerStyles[urgency] || bannerStyles.warning;
  const Icon = style.icon;

  // Generate message based on urgency
  const getMessage = () => {
    if (urgency === 'overdue') {
      return `Your EEOC compliance training is ${Math.abs(days_remaining)} days overdue. Please complete it immediately.`;
    } else if (urgency === 'urgent') {
      return `Only ${days_remaining} day${days_remaining !== 1 ? 's' : ''} left to complete your EEOC compliance training!`;
    } else {
      return `You have ${days_remaining} days to complete your mandatory EEOC compliance training.`;
    }
  };

  const trainingPath = userUrl ? `/user/${userUrl}/eeoc` : '/dashboard/eeoc';

  return (
    <div 
      className={cn(
        'border rounded-lg p-4 mb-4 flex items-center justify-between gap-4',
        style.bg,
        className
      )}
      data-testid="eeoc-reminder-banner"
    >
      <div className="flex items-center gap-3">
        <div className={cn('p-2 rounded-full bg-white/50 dark:bg-black/20', style.iconColor)}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <div className="flex items-center gap-2">
            <Shield className={cn('h-4 w-4', style.iconColor)} />
            <span className={cn('font-semibold', style.textColor)}>
              EEOC Training Required
            </span>
          </div>
          <p className={cn('text-sm mt-0.5', style.textColor)}>
            {getMessage()}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Link to={trainingPath}>
          <Button size="sm" className={style.buttonClass}>
            Start Training
            <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
        </Link>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleDismiss}
          className={cn('hover:bg-white/50 dark:hover:bg-black/20', style.textColor)}
          title="Dismiss for 24 hours"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
